import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import StatusBadge from "@/components/StatusBadge";
import Modal, { Field, TextInput, Select, PrimaryButton, SecondaryButton } from "@/components/FormKit";
import { toast } from "sonner";
import { fmtCurrency, fmtDate } from "@/lib/format";

const initial = { customer_id: "", unit_id: "", milestone: "", amount: "", due_date: "", description: "" };

export default function Schedules() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [units, setUnits] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);

  const load = async () => {
    const [s, c, u] = await Promise.all([
      api.get("/company/schedules"),
      api.get("/company/customers"),
      api.get("/company/units"),
    ]);
    setItems(s.data);
    setCustomers(c.data);
    setUnits(u.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/company/schedules", { ...form, amount: Number(form.amount) });
      toast.success("Schedule created");
      setOpen(false);
      setForm(initial);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-8" data-testid="company-schedules-page">
      <PageHeader
        eyebrow="Finance"
        title="Payment schedules"
        description="Milestone-based due dates per customer unit."
        actions={<PrimaryButton onClick={() => setOpen(true)} testId="new-schedule-button">+ New Schedule</PrimaryButton>}
      />

      <DataTable
        testId="schedules-table"
        columns={[
          { key: "customer", label: "Customer", render: (r) => <div className="font-bold">{r.customer?.name || "—"}</div> },
          { key: "unit", label: "Unit", render: (r) => <span className="font-mono">{r.unit?.unit_no || "—"}</span> },
          { key: "milestone", label: "Milestone" },
          { key: "amount", label: "Amount", render: (r) => <span className="font-mono">{fmtCurrency(r.amount)}</span> },
          { key: "due_date", label: "Due", render: (r) => fmtDate(r.due_date) },
          { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
        ]}
        rows={items}
        empty="No schedules yet."
      />

      <Modal open={open} onClose={() => setOpen(false)} title="New payment schedule">
        <form onSubmit={submit}>
          <Field label="Customer">
            <Select testId="form-sched-customer" required value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}>
              <option value="">Select customer…</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Unit">
            <Select testId="form-sched-unit" required value={form.unit_id} onChange={(e) => setForm({ ...form, unit_id: e.target.value })}>
              <option value="">Select unit…</option>
              {units.map((u) => <option key={u.id} value={u.id}>{u.project?.name} · {u.unit_no}</option>)}
            </Select>
          </Field>
          <Field label="Milestone"><TextInput testId="form-sched-milestone" required value={form.milestone} onChange={(e) => setForm({ ...form, milestone: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount (INR)"><TextInput testId="form-sched-amount" type="number" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Field>
            <Field label="Due date"><TextInput testId="form-sched-date" type="date" required value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></Field>
          </div>
          <Field label="Description"><TextInput testId="form-sched-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <div className="flex justify-end gap-2 pt-2">
            <SecondaryButton type="button" onClick={() => setOpen(false)}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" testId="submit-schedule">Create</PrimaryButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
